import { Link } from 'react-router-dom';
import { Clock, AlertCircle, Building2, Mail } from 'lucide-react';
import { useAgingReport } from '@/hooks/useFinancials';
import { AGING_BUCKET_LABELS, type AgingBucket } from '@/types/financials';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency } from '@/lib/utils';

const BUCKET_ORDER: AgingBucket[] = ['current', '1-30', '31-60', '61-90', '90+'];

const BUCKET_COLORS: Record<AgingBucket, string> = {
  current: 'bg-success',
  '1-30':  'bg-brass-400',
  '31-60': 'bg-warning',
  '61-90': 'bg-danger',
  '90+':   'bg-danger',
};

const BUCKET_TEXT_COLORS: Record<AgingBucket, string> = {
  current: 'text-success',
  '1-30':  'text-brass-300',
  '31-60': 'text-warning',
  '61-90': 'text-danger',
  '90+':   'text-danger',
};

export function AgingPage() {
  const { data, isLoading, error } = useAgingReport();

  if (isLoading) return <LoaderScreen />;

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load aging report"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  if (!data || data.totalReceivable === 0) {
    return (
      <Card>
        <EmptyState
          icon={Clock}
          title="Nothing on the books"
          description="No outstanding invoices. When you send your first invoice, AR aging will appear here."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary buckets */}
      <Card>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="kicker mb-1">Total Accounts Receivable</div>
            <div className="font-display text-4xl text-brass-300 tabular">
              {formatCurrency(data.totalReceivable)}
            </div>
          </div>
          <div className="text-right">
            <div className="kicker mb-1">Past Due</div>
            <div className="font-display text-2xl text-danger tabular">
              {formatCurrency(data.buckets['1-30'] + data.buckets['31-60'] + data.buckets['61-90'] + data.buckets['90+'])}
            </div>
          </div>
        </div>

        {/* Stacked bar */}
        <div className="h-3 rounded-sm overflow-hidden flex bg-bg-subtle mb-3">
          {BUCKET_ORDER.map((b) => {
            const amt = data.buckets[b];
            if (amt === 0) return null;
            const pct = (amt / data.totalReceivable) * 100;
            return (
              <div
                key={b}
                className={`${BUCKET_COLORS[b]} transition-all`}
                style={{ width: `${pct}%` }}
                title={`${AGING_BUCKET_LABELS[b]}: ${formatCurrency(amt)}`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="grid grid-cols-5 gap-2">
          {BUCKET_ORDER.map((b) => (
            <div key={b} className="text-center">
              <div className="kicker mb-1">{AGING_BUCKET_LABELS[b]}</div>
              <div className={`text-sm tabular ${BUCKET_TEXT_COLORS[b]}`}>
                {formatCurrency(data.buckets[b])}
              </div>
              <div className="text-2xs text-text-dim mt-0.5">
                {data.totalReceivable > 0 ? `${((data.buckets[b] / data.totalReceivable) * 100).toFixed(0)}%` : '0%'}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* By client */}
      <Card className="p-0 overflow-hidden">
        <header className="px-6 py-4 border-b border-border">
          <div className="kicker mb-1">By Client · {data.byClient.length}</div>
          <h3 className="font-display text-lg text-text">Outstanding Balances</h3>
        </header>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="kicker px-6 py-3 font-normal">Client</th>
              <th className="kicker px-6 py-3 font-normal text-right">Current</th>
              <th className="kicker px-6 py-3 font-normal text-right">1–30</th>
              <th className="kicker px-6 py-3 font-normal text-right">31–60</th>
              <th className="kicker px-6 py-3 font-normal text-right">61–90</th>
              <th className="kicker px-6 py-3 font-normal text-right">90+</th>
              <th className="kicker px-6 py-3 font-normal text-right">Total</th>
              <th className="kicker px-6 py-3 font-normal w-12" />
            </tr>
          </thead>
          <tbody>
            {data.byClient
              .sort((a, b) => b.total - a.total)
              .map((client) => {
                const past90 = client.buckets['90+'];
                const past60 = client.buckets['61-90'];
                return (
                  <tr key={client.clientId} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                    <td className="px-6 py-4">
                      <Link to={`/clients/${client.clientId}`} className="flex items-center gap-2 text-text hover:text-brass-300">
                        <Building2 size={13} className="text-text-dim" />
                        {client.clientName}
                      </Link>
                      {client.oldestDueDate && (
                        <div className="text-2xs text-text-dim mt-0.5">
                          Oldest: {new Date(client.oldestDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </td>
                    <Cell value={client.buckets.current} tone="neutral" />
                    <Cell value={client.buckets['1-30']} tone="brass" />
                    <Cell value={client.buckets['31-60']} tone="warning" />
                    <Cell value={client.buckets['61-90']} tone="danger" />
                    <Cell value={client.buckets['90+']} tone="danger" />
                    <td className="px-6 py-4 text-right tabular font-medium text-text">
                      {formatCurrency(client.total)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {(past90 > 0 || past60 > 0) && (
                        <Mail size={13} className="text-danger inline" aria-label="Collections needed" />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot className="bg-bg-subtle">
            <tr className="border-t border-border">
              <td className="px-6 py-4 text-text">
                <span className="kicker">Totals</span>
              </td>
              {BUCKET_ORDER.map((b) => (
                <td key={b} className={`px-6 py-4 text-right tabular ${BUCKET_TEXT_COLORS[b]}`}>
                  {formatCurrency(data.buckets[b])}
                </td>
              ))}
              <td className="px-6 py-4 text-right tabular font-display text-brass-300">
                {formatCurrency(data.totalReceivable)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

function Cell({ value, tone }: { value: number; tone: 'neutral' | 'brass' | 'warning' | 'danger' }) {
  const colorClass = {
    neutral: 'text-text-muted',
    brass:   'text-brass-300',
    warning: 'text-warning',
    danger:  'text-danger',
  }[tone];
  return (
    <td className={`px-6 py-4 text-right tabular ${colorClass}`}>
      {value > 0 ? formatCurrency(value) : <span className="text-text-dim">—</span>}
    </td>
  );
}
